import {
	addDays,
	addHours,
	addMinutes,
	format,
	isSameDay,
	isSameHour,
	isSameMinute,
	parse,
} from "date-fns";

export function prepareCargosData({ cargos, aiRules, timePreferences }) {
	return cargos.map((item) => {
		const {
			vehicleTypeId,
			vehicleBodyId,
			loadingTime,
			unloadingTime,
			weight,
			length,
			loadingDate,
			isFull,
			isLTL,
		} = item;

		// For coloring logic
		const definedColors = defineItemColor({ item });

		const loadDate = loadingDate
			? loadingDate
			: aiRules["loadingDate"]
			? handleLoadingDate({
					rule: aiRules["loadingDate"],
			  })
			: "";

		const isLoadingTimeValid = loadingTime && loadingTime.includes(":");
		const isUnloadingTimeValid = unloadingTime && unloadingTime.includes(":");

		const loadTime = isLoadingTimeValid
			? handleTimeFromAI({ time: loadingTime, date: loadDate, isUnload: false })
			: aiRules["times"]
			? handleTimeFromPreferences({
					preferredStartTime: timePreferences["loadFrom"],
					preferredEndTime: timePreferences["loadTo"],
					date: loadDate,
					loadDate: loadDate,
					unloadDate: item.unloadingDate,
					preferredLoadStartTime: timePreferences["loadFrom"],
					preferredLoadEndTime: timePreferences["loadTo"],
					preferredUnloadStartTime: timePreferences["unloadFrom"],
					preferredUnloadEndTime: timePreferences["unloadTo"],
					isUnload: false,
			  })
			: null;

		const unloadTime = isUnloadingTimeValid
			? handleTimeFromAI({
					time: unloadingTime,
					date: item.unloadingDate,
					isUnload: true,
			  })
			: aiRules["times"]
			? handleTimeFromPreferences({
					preferredStartTime: timePreferences["unloadFrom"],
					preferredEndTime: timePreferences["unloadTo"],
					date: item.unloadingDate,
					loadDate: loadDate,
					unloadDate: item.unloadingDate,
					preferredLoadStartTime: timePreferences["loadFrom"],
					preferredLoadEndTime: timePreferences["loadTo"],
					preferredUnloadStartTime: timePreferences["unloadFrom"],
					preferredUnloadEndTime: timePreferences["unloadTo"],
					isUnload: true,
			  })
			: null;

		const finalWeight =
			weight > 0
				? weight.toFixed(1)
				: isFull
				? 24
				: aiRules["weight"]
				? Number(aiRules["weight"]).toFixed(1)
				: null;

		const finalLength =
			length > 0
				? length.toFixed(1)
				: isFull
				? 13.6
				: aiRules["length"]
				? Number(aiRules["length"]).toFixed(1)
				: null;

		const finalVehicleTypeId = vehicleTypeId?.length
			? vehicleTypeId
			: finalWeight > 10 || finalLength > 10
			? ["TRAILER"]
			: aiRules["vehicleTypeId"]
			? aiRules["vehicleTypeId"]
			: [];

		const finalVehicleBodyId = vehicleBodyId?.length
			? vehicleBodyId
			: finalWeight > 10 || finalLength > 10
			? ["CURTAIN_SIDER", "TAUTLINER"]
			: aiRules["vehicleBodyId"]
			? aiRules["vehicleBodyId"]
			: [];

		const finalFtl =
			isFull === true
				? "ftl"
				: isLTL === true
				? "ltl"
				: aiRules["ftl"]
				? aiRules["ftl"]
				: null;

		return {
			...item,
			ftl: {
				data: finalFtl,
				color: definedColors["ftl"],
			},
			vehicleTypeId: {
				data: finalVehicleTypeId,
				color: definedColors["vehicleTypeId"],
			},
			vehicleBodyId: {
				data: finalVehicleBodyId,
				color: definedColors["vehicleBodyId"],
			},
			loadingDate: {
				data: loadDate,
				color: definedColors["loadingDate"],
			},
			loadingTime: {
				data: loadTime,
				color: definedColors["loadingTime"],
			},
			unloadingTime: {
				data: unloadTime,
				color: definedColors["unloadingTime"],
			},
			weight: {
				data: finalWeight,
				color: definedColors["weight"],
			},
			length: {
				data: finalLength,
				color: definedColors["length"],
			},
			// for quality check
			raw: item,
		};
	});
}

export function handleTimeFromAI({ time, date, isUnload }) {
	const dateType = date.includes(":") ? "range" : "day";

	const [timeStart, timeEnd] = time.split("-");

	const currentTime = new Date();
	const currentMinutes = currentTime.getMinutes();
	const currentTimeWithoutMinutes = new Date(currentTime);
	currentTimeWithoutMinutes.setMinutes(0, 0, 0);

	const parsedStartTime = parse(timeStart, "HH:mm", new Date());
	const parsedEndTime = parse(timeEnd, "HH:mm", new Date());

	const isPastCustomTimeStart = currentTime > parsedStartTime;
	// const isPastCustomTimeEnd = currentTime > parsedEndTime;
	let minStartTime = new Date(currentTimeWithoutMinutes);

	if (dateType === "day") {
		const pickedDate = date ? new Date(date) : null;

		if (pickedDate) {
			const isPickedDateToday =
				format(pickedDate, "yyyy-MM-dd") === format(currentTime, "yyyy-MM-dd");

			if (isPickedDateToday && isPastCustomTimeStart) {
				if (currentMinutes >= 45) {
					minStartTime = addHours(minStartTime, 2);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				} else {
					minStartTime = addHours(minStartTime, 1);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				}

				if (timeEnd) {
					const minEndTime = isPastCustomTimeStart
						? parsedEndTime > addMinutes(minStartTime, 30)
							? parsedEndTime
							: addMinutes(minStartTime, 30)
						: parsedEndTime;

					return [
						format(minStartTime, "HH:mm"),
						format(minEndTime, "HH:mm"),
					].join("-");
				}

				return [format(minStartTime, "HH:mm")].join("-");
			}
		}
	}

	if (dateType === "range") {
		const [startDate] = date.split(":");

		if (startDate) {
			const pickedDate = new Date(startDate);

			const isPickedDateToday =
				format(pickedDate, "yyyy-MM-dd") === format(currentTime, "yyyy-MM-dd");

			if (isPickedDateToday && isPastCustomTimeStart) {
				if (currentMinutes >= 45) {
					minStartTime = addHours(minStartTime, 2);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				} else {
					minStartTime = addHours(minStartTime, 1);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				}

				if (timeEnd) {
					const minEndTime = isPastCustomTimeStart
						? parsedEndTime > addMinutes(minStartTime, 30)
							? parsedEndTime
							: addMinutes(minStartTime, 30)
						: parsedEndTime;

					return [
						format(minStartTime, "HH:mm"),
						format(minEndTime, "HH:mm"),
					].join("-");
				}

				return [format(minStartTime, "HH:mm")].join("-");
			}
		}
	}

	if (timeEnd) {
		return [
			format(new Date(parsedStartTime), "HH:mm"),
			format(new Date(parsedEndTime), "HH:mm"),
		].join("-");
	}

	return [format(new Date(parsedStartTime), "HH:mm")].join("-");
}

export function handleTimeFromPreferences({
	preferredStartTime,
	preferredEndTime,
	date,
	loadDate,
	unloadDate,
	preferredLoadStartTime,
	preferredLoadEndTime,
	preferredUnloadStartTime,
	preferredUnloadEndTime,
	isUnload,
}) {
	const dateType = date.includes(":") ? "range" : "day";
	const loadDateType = loadDate.includes(":") ? "range" : "day";
	const unloadDateType = unloadDate.includes(":") ? "range" : "day";

	const currentTime = new Date();
	const currentMinutes = currentTime.getMinutes();
	const currentTimeWithoutMinutes = new Date(currentTime);
	currentTimeWithoutMinutes.setMinutes(0, 0, 0);

	// fallback if not set preferences but set to use preferences in AI rules
	const isEveryPreferenceNull =
		!preferredLoadStartTime &&
		!preferredLoadEndTime &&
		!preferredUnloadStartTime &&
		!preferredUnloadEndTime;

	const fallbackStartTime = new Date();
	fallbackStartTime.setMinutes(0, 0, 0);
	fallbackStartTime.setHours(8, 0, 0);

	const fallbackEndTime = new Date();
	fallbackEndTime.setMinutes(0, 0, 0);
	fallbackEndTime.setHours(16, 0, 0);

	const parsedPreferredStartTime = preferredStartTime
		? new Date(preferredStartTime)
		: fallbackStartTime;
	const parsedPreferredEndTime = preferredEndTime
		? new Date(preferredEndTime)
		: fallbackEndTime;

	parsedPreferredStartTime.setFullYear(
		currentTime.getFullYear(),
		currentTime.getMonth(),
		currentTime.getDate()
	);

	parsedPreferredEndTime.setFullYear(
		currentTime.getFullYear(),
		currentTime.getMonth(),
		currentTime.getDate()
	);

	const parsedPreferredLoadStartTime = preferredLoadStartTime
		? new Date(preferredLoadStartTime)
		: fallbackStartTime;
	const parsedPreferredLoadEndTime = preferredLoadEndTime
		? new Date(preferredLoadEndTime)
		: fallbackEndTime;

	parsedPreferredLoadStartTime.setFullYear(
		currentTime.getFullYear(),
		currentTime.getMonth(),
		currentTime.getDate()
	);

	parsedPreferredLoadEndTime.setFullYear(
		currentTime.getFullYear(),
		currentTime.getMonth(),
		currentTime.getDate()
	);

	const parsedPreferredUnloadStartTime = preferredUnloadStartTime
		? new Date(preferredUnloadStartTime)
		: fallbackStartTime;
	const parsedPreferredUnloadEndTime = preferredUnloadEndTime
		? new Date(preferredUnloadEndTime)
		: fallbackEndTime;

	parsedPreferredUnloadStartTime.setFullYear(
		currentTime.getFullYear(),
		currentTime.getMonth(),
		currentTime.getDate()
	);

	parsedPreferredUnloadEndTime.setFullYear(
		currentTime.getFullYear(),
		currentTime.getMonth(),
		currentTime.getDate()
	);

	const isPastCustomTimeStart = currentTime > parsedPreferredStartTime;
	// const isPastCustomTimeEnd = currentTime > parsedPreferredEndTime;
	let minStartTime = new Date(currentTimeWithoutMinutes);

	if (
		loadDateType === "day" &&
		unloadDateType === "day" &&
		loadDate &&
		unloadDate
	) {
		const isTheSameDay = isSameDay(new Date(loadDate), new Date(unloadDate));
		const isTheSameHour = isSameHour(
			parsedPreferredLoadStartTime,
			parsedPreferredUnloadStartTime
		);
		const isTheSameMinute = isSameMinute(
			parsedPreferredLoadStartTime,
			parsedPreferredUnloadStartTime
		);

		if (isTheSameDay && isTheSameHour && isTheSameMinute && isUnload) {
			minStartTime = addMinutes(parsedPreferredUnloadStartTime, 30);

			if (preferredEndTime || isEveryPreferenceNull) {
				const minEndTime = isPastCustomTimeStart
					? parsedPreferredEndTime > addMinutes(minStartTime, 30)
						? parsedPreferredEndTime
						: addMinutes(minStartTime, 30)
					: parsedPreferredEndTime;

				return [
					format(minStartTime, "HH:mm"),
					format(minEndTime, "HH:mm"),
				].join("-");
			}

			return [format(minStartTime, "HH:mm")].join("-");
		}
	}

	if (dateType === "day") {
		const pickedDate = date ? new Date(date) : null;

		if (pickedDate) {
			const isPickedDateToday =
				format(pickedDate, "yyyy-MM-dd") === format(currentTime, "yyyy-MM-dd");

			if (isPickedDateToday && isPastCustomTimeStart) {
				if (currentMinutes >= 45) {
					minStartTime = addHours(minStartTime, 2);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				} else {
					minStartTime = addHours(minStartTime, 1);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				}

				if (preferredEndTime || isEveryPreferenceNull) {
					const minEndTime = isPastCustomTimeStart
						? parsedPreferredEndTime > addMinutes(minStartTime, 30)
							? parsedPreferredEndTime
							: addMinutes(minStartTime, 30)
						: parsedPreferredEndTime;

					return [
						format(minStartTime, "HH:mm"),
						format(minEndTime, "HH:mm"),
					].join("-");
				}

				return [format(minStartTime, "HH:mm")].join("-");
			}
		}
	}

	if (dateType === "range") {
		const [startDate] = date.split(":");

		if (startDate) {
			const pickedDate = new Date(startDate);

			const isPickedDateToday =
				format(pickedDate, "yyyy-MM-dd") === format(currentTime, "yyyy-MM-dd");

			if (isPickedDateToday && isPastCustomTimeStart) {
				if (currentMinutes >= 45) {
					minStartTime = addHours(minStartTime, 2);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				} else {
					minStartTime = addHours(minStartTime, 1);
					if (isUnload) {
						minStartTime = addMinutes(minStartTime, 30);
					}
				}

				if (preferredEndTime || isEveryPreferenceNull) {
					const minEndTime = isPastCustomTimeStart
						? parsedPreferredEndTime > addMinutes(minStartTime, 30)
							? parsedPreferredEndTime
							: addMinutes(minStartTime, 30)
						: parsedPreferredEndTime;

					return [
						format(minStartTime, "HH:mm"),
						format(minEndTime, "HH:mm"),
					].join("-");
				}

				return [format(minStartTime, "HH:mm")].join("-");
			}
		}
	}

	if (preferredEndTime || isEveryPreferenceNull) {
		return [
			format(new Date(parsedPreferredStartTime), "HH:mm"),
			format(new Date(parsedPreferredEndTime), "HH:mm"),
		].join("-");
	}

	return [format(new Date(parsedPreferredStartTime), "HH:mm")].join("-");
}

export function defineItemColor({ item }) {
	const {
		vehicleTypeId,
		vehicleBodyId,
		loadingTime,
		unloadingTime,
		weight,
		length,
		loadingDate,
		isFull,
		isLTL,
	} = item;

	const defaultColor = "#D6EEFF";
	const aiRulesColor = "#c8ceff";

	const isFtlFound = isFull;
	const isLtlFound = isLTL;
	const isVehicleTypeIdFound =
		vehicleTypeId?.length || weight > 10 || length > 10;
	const isVehicleBodyIdFound =
		vehicleBodyId?.length || weight > 10 || length > 10;
	const isFoundLoadingTime = !!loadingTime;
	const isFoundUnloadingTime = !!unloadingTime;
	const isWeightFound = weight > 0 || isFull;
	const isLengthFound = length > 0 || isFull;
	const isFoundLoadingData = !!loadingDate;

	return {
		ftl: isFtlFound || isLtlFound ? defaultColor : aiRulesColor,
		vehicleTypeId: isVehicleTypeIdFound ? defaultColor : aiRulesColor,
		vehicleBodyId: isVehicleBodyIdFound ? defaultColor : aiRulesColor,
		loadingTime: isFoundLoadingTime ? defaultColor : aiRulesColor,
		unloadingTime: isFoundUnloadingTime ? defaultColor : aiRulesColor,
		weight: isWeightFound ? defaultColor : aiRulesColor,
		length: isLengthFound ? defaultColor : aiRulesColor,
		loadingDate: isFoundLoadingData ? defaultColor : aiRulesColor,
	};
}

function handleLoadingDate({ rule }) {
	const today = new Date();

	switch (rule) {
		case "current-day":
			return format(today, "yyyy-MM-dd");
		case "next-day":
			return format(addDays(today, 1), "yyyy-MM-dd");
		case "range-today-1":
			return `${format(today, "yyyy-MM-dd")}:${format(
				addDays(today, 1),
				"yyyy-MM-dd"
			)}`;
		case "range-today-2":
			return `${format(today, "yyyy-MM-dd")}:${format(
				addDays(today, 2),
				"yyyy-MM-dd"
			)}`;
		case "range-tomorrow-1":
			return `${format(addDays(today, 1), "yyyy-MM-dd")}:${format(
				addDays(today, 2),
				"yyyy-MM-dd"
			)}`;
		case "range-tomorrow-2":
			return `${format(addDays(today, 1), "yyyy-MM-dd")}:${format(
				addDays(today, 3),
				"yyyy-MM-dd"
			)}`;
		default:
			return "";
	}
}
